/**
 * API service for backend communication
 */

import type {
  MunicipalitySearchResponse,
  Municipality,
  MunicipalitySnapshot,
  CountySnapshot,
  StateSnapshot,
  LagepromptResponse,
  County,
  MakrolageData,
  StateData,
  FederalData
} from '../types/api'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8899'

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(
      error.detail || 'An error occurred',
      response.status,
      error
    )
  }
  return response.json()
}

export const api = {
  /**
   * Search municipalities
   */
  async searchMunicipalities(
    query?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<MunicipalitySearchResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    })
    if (query) {
      params.set('q', query)
    }

    const response = await fetch(`${API_BASE_URL}/api/municipalities/?${params}`)
    return handleResponse<MunicipalitySearchResponse>(response)
  },

  /**
   * Get municipality by AGS (municipality key)
   */
  async getMunicipalityByKey(municipalityKey: string): Promise<Municipality> {
    const response = await fetch(`${API_BASE_URL}/api/municipalities/key/${municipalityKey}`)
    return handleResponse<Municipality>(response)
  },



  /**
   * Get county details by key (includes BBSR mappings)
   */
  async getCountyByKey(countyKey: string): Promise<County> {
    const response = await fetch(`${API_BASE_URL}/api/counties/key/${countyKey}`)
    return handleResponse<County>(response)
  },


  /**
   * Get Lageprompt (Lagedaten-Block + fertiger KI-Prompt) by AGS
   */
  async getMunicipalityLageprompt(municipalityKey: string): Promise<LagepromptResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/reports/municipalities/${municipalityKey}/lageprompt`
    )
    return handleResponse<LagepromptResponse>(response)
  },

  /**
   * Download-URL für die Lagedaten als Markdown-Datei (.md)
   */
  lagedatenMarkdownUrl(municipalityKey: string): string {
    return `${API_BASE_URL}/api/reports/municipalities/${municipalityKey}/lagedaten.md`
  },


  /**
   * Get comprehensive Makrolage (macro-location) data for a county
   */
  async getCountyMakrolage(countyId: number): Promise<MakrolageData> {
    const response = await fetch(`${API_BASE_URL}/api/counties/${countyId}/makrolage`)
    return handleResponse<MakrolageData>(response)
  },

  /**
   * Get state-level (Bundesland) data
   */
  async getStateData(stateId: number): Promise<StateData> {
    const response = await fetch(`${API_BASE_URL}/api/states/${stateId}/data`)
    return handleResponse<StateData>(response)
  },

  /**
   * Get federal-level (Bund) data for Germany
   * @param year - Optional: specific snapshot year
   */
  async getFederalData(year?: number): Promise<FederalData> {
    const params = new URLSearchParams()
    if (year) params.set('year', year.toString())
    const queryString = params.toString()
    const url = queryString
      ? `${API_BASE_URL}/api/federal/data?${queryString}`
      : `${API_BASE_URL}/api/federal/data`
    const response = await fetch(url)
    return handleResponse<FederalData>(response)
  },

  /**
   * Get available editions for a municipality
   */
  async getMunicipalityAvailableEditions(municipalityKey: string): Promise<number[]> {
    const response = await fetch(`${API_BASE_URL}/api/municipalities/key/${municipalityKey}/available-editions`)
    return handleResponse<number[]>(response)
  },

  /**
   * Get municipality snapshot for a specific edition
   */
  async getMunicipalitySnapshot(municipalityKey: string, edition: number): Promise<MunicipalitySnapshot> {
    const response = await fetch(`${API_BASE_URL}/api/municipalities/key/${municipalityKey}/snapshot/${edition}`)
    return handleResponse<MunicipalitySnapshot>(response)
  },

  /**
   * Get all snapshots for a municipality
   */
  async getMunicipalitySnapshots(municipalityKey: string): Promise<MunicipalitySnapshot[]> {
    const response = await fetch(`${API_BASE_URL}/api/municipalities/key/${municipalityKey}/snapshots`)
    return handleResponse<MunicipalitySnapshot[]>(response)
  },

  /**
   * Get available editions for a county
   */
  async getCountyAvailableEditions(countyKey: string): Promise<number[]> {
    const response = await fetch(`${API_BASE_URL}/api/counties/key/${countyKey}/available-editions`)
    return handleResponse<number[]>(response)
  },

  /**
   * Get county snapshot for a specific edition
   */
  async getCountySnapshot(countyKey: string, edition: number): Promise<CountySnapshot> {
    const response = await fetch(`${API_BASE_URL}/api/counties/key/${countyKey}/snapshot/${edition}`)
    return handleResponse<CountySnapshot>(response)
  },

  /**
   * Get available editions for a state
   */
  async getStateAvailableEditions(stateCode: string): Promise<number[]> {
    const response = await fetch(`${API_BASE_URL}/api/states/code/${stateCode}/available-editions`)
    return handleResponse<number[]>(response)
  },

  /**
   * Get state snapshot for a specific edition
   */
  async getStateSnapshot(stateCode: string, edition: number): Promise<StateSnapshot> {
    const response = await fetch(`${API_BASE_URL}/api/states/code/${stateCode}/snapshot/${edition}`)
    return handleResponse<StateSnapshot>(response)
  },

  /**
   * Get all text indicators (Textkennzeichen) for lookup
   */
  async getTextIndicators(): Promise<TextIndicatorListResponse> {
    const response = await fetch(`${API_BASE_URL}/admin/classifications/text-indicators?limit=100`)
    return handleResponse<TextIndicatorListResponse>(response)
  },

  /**
   * Get field labels for a data source by provider
   */
  async getFieldLabelsByProvider(provider: string): Promise<FieldLabel[]> {
    const response = await fetch(`${API_BASE_URL}/api/data-sources/field-labels/by-provider/${provider}`)
    return handleResponse<FieldLabel[]>(response)
  },

  /**
   * Get indicator values for an entity (municipality, county, state, etc.)
   * @param entityType - One of: municipality, county, state, government_district, federal
   * @param ags - AGS code (8-digit for municipality, 5-digit for county, 2-digit for state, etc.)
   * @param year - Optional: filter by reference year
   */
  async getEntityIndicators(
    entityType: string,
    ags: string,
    year?: number,
    maxYear?: number
  ): Promise<EntityIndicatorsResponse> {
    const params = new URLSearchParams()
    if (year) params.set('year', year.toString())
    if (maxYear) params.set('max_year', maxYear.toString())
    const queryString = params.toString()
    const url = queryString
      ? `${API_BASE_URL}/api/indicators/${entityType}/${ags}?${queryString}`
      : `${API_BASE_URL}/api/indicators/${entityType}/${ags}`
    const response = await fetch(url)
    return handleResponse<EntityIndicatorsResponse>(response)
  },

  /**
   * Get available indicators for an entity type
   */
  async getAvailableIndicators(entityType: string): Promise<AvailableIndicatorsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/indicators/${entityType}`)
    return handleResponse<AvailableIndicatorsResponse>(response)
  },
}

// Text Indicator types
export interface TextIndicator {
  id: number
  code: string
  category: string
  name: string
  description: string | null
}

// Field Label types
export interface FieldLabel {
  field_name: string
  display_label: string
  default_label: string
  custom_label: string | null
  description: string | null
  display_order: number
  is_visible: boolean
}

export interface TextIndicatorListResponse {
  total: number
  limit: number
  offset: number
  results: TextIndicator[]
}

// Indicator types (Regionalstatistik and INKAR)
export interface IndicatorValue {
  indicator_id: number
  indicator_code: string
  indicator_name: string
  unit: string | null
  value: number | null
  value_text: string | null
  reference_year: number
  year: number | null  // The actual data year (may differ from reference_year)
  category: string | null  // Main category (e.g., "Zentrale Orte Monitoring")
  subcategory: string | null  // Subcategory (e.g., "Gesundheitsversorgungsfunktion")
}

export interface IndicatorDataSource {
  id: number
  provider: string
  dataset_name: string | null
  edition: string | null
  statistics_code: string | null
  statistics_label: string | null
  table_code: string | null
  time_label: string | null
  time_value: string | null
  reference_year: number
  import_date: string
  records_imported: number | null
}

export interface IndicatorGroup {
  data_source: IndicatorDataSource
  indicators: IndicatorValue[]
}

export interface EntityIndicatorsResponse {
  entity_type: string
  entity_id: number
  entity_ags: string
  entity_name: string
  groups: IndicatorGroup[]
  total_indicators: number
}

export interface IndicatorInfo {
  id: number
  code: string
  name: string
  unit: string | null
  category: string | null
  source: string | null
  description: string | null
}

export interface AvailableIndicatorsResponse {
  entity_type: string
  indicators: IndicatorInfo[]
  data_sources: IndicatorDataSource[]
}

export { ApiError }
